import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import type { AppDispatch, RootState } from "../../../redux/store";

/* ⭐ SAME SLICE the fresh seller writes to — FreshStore saves the
   handling fields (unit, netContent, shelfLifeDays, harvestedOn,
   bestBefore, batchCode, allergens, portions, extras, diet flags). */
import {
  listenToPublishedFoods,
  filterLocalFoodsByCountry,
  setFilters,
  FoodItem,
} from "../../../redux/foodSlice";
import { addToCart, decrementCartItem, clearCart } from "../../../redux/cartSlice";
import {
  type RateType,
  DEFAULT_RATE_TYPE,
  calculateLineTotal,
} from "../../../redux/foodServiceFeeSlice";
import { listenToBrandsByBusinessType } from "../../../redux/commercePartnersSlice";
import { fetchAllProductSummaries } from "../../../redux/productRatingSlice";
import { CurrencyContext } from "../../../GLOBAL/CurrencyContext";
import appLogo from "../../../assets/app/logo.png";

/* ⭐ CATEGORY SOURCE — FreshStore saves `categoryId` as a
   CategoriesCollection id inside the Food department, plus
   `attributes` keyed by attribute id. This page walks the same
   tree back up, exactly like StoreProductsHome does. */
import {
  DEPARTMENTS,
  AllCategoriesFlat,
  type CategoryItem,
} from "../../../CATEGORIES/CategoriesCollection";
import { attributesForCategory } from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";

import {
  Search, ShoppingBag, X, ChevronRight, Plus, Minus,
  User, ShoppingCart, MapPin, Globe, AlertTriangle,
  Home, ShoppingBasket, LayoutGrid, Check, ChevronDown,
  Leaf, Sprout, Snowflake, Sun, Clock, Package, Sparkles, WheatOff, Flame, Tag,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   SCOPE — the three departments this marketplace covers. The
   oval bar toggles between them; everything below drills the
   standard hierarchy inside whichever one is active:
   department → category → subCategory → productType → product.
═══════════════════════════════════════════════════════════ */
const FRESH_DEPT_IDS = ["08"];   // Food — fastfood/grocery/farm all live under here now
const DEFAULT_DEPT_ID = "0801";          // Fast food leads — must be a branch, not the department

/** Which commercePartners show up here. Flip if your seed data differs. */
const FRESH_BUSINESS_TYPE = 9;

/* ═══════════════════════════════════════════════════════════
   HEARTLAS DESIGN TOKENS — same palette as StoreProductsHome,
   with the seller-side green kept for organic / freshness.
═══════════════════════════════════════════════════════════ */
const HEART = {
  bg: "#FBF6EE",
  panelBg: "#FFFFFF",

  dark: "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",

  red: "#cb0011",
  redDark: "#9E1521",
  redSoft: "#FBE7E9",

  gold: "#F2A93E",
  goldDark: "#D4900F",
  goldSoft: "#FCEFD4",

  green: "#3F8A4C",
  greenSoft: "#E7F3E8",

  border: "#EEDFC4",
  divider: "#F3E6CE",
};

const T = {
  pageBg: HEART.bg,
  cardBg: HEART.panelBg,
  black: HEART.dark,
  muted: HEART.textMuted,
  mutedDim: HEART.textFaint,
  red: HEART.red,
  redDim: HEART.redSoft,
  border: HEART.border,
  grad: `linear-gradient(135deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  gradH: `linear-gradient(90deg, ${HEART.red} 0%, ${HEART.redDark} 100%)`,
  outline: `1.5px solid ${HEART.border}`,
};

const GRAD_H = T.gradH;
const RGBA_RED = (a: number) => `rgba(200,32,46,${a})`;

/* accent comes straight from the grower's brand — no random tints */
const brandAccentColor = (partner: any): string =>
  partner?.brandColor || partner?.themeColor || partner?.color || HEART.red;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE HELPERS — ancestry is ALWAYS resolved by walking
   parentId, never by string prefixes (PT-… / PRD-… break those).
═══════════════════════════════════════════════════════════ */
const CAT_MAP = new Map<string, CategoryItem>();
AllCategoriesFlat.forEach((c) => CAT_MAP.set(c.id, c));

const childrenOf = (parentId: string | null) =>
  AllCategoriesFlat.filter((c) => c.parentId === parentId);

const chainCache = new Map<string, string[]>();
const chainIdsOf = (id?: string | number | null): string[] => {
  if (id === null || id === undefined || id === "") return [];
  const key = String(id);
  const hit = chainCache.get(key);
  if (hit) return hit;
  const chain: string[] = [];
  let cur = CAT_MAP.get(key);
  while (cur) {
    chain.unshift(cur.id);
    cur = cur.parentId ? CAT_MAP.get(cur.parentId) : undefined;
  }
  chainCache.set(key, chain);
  return chain;
};

const catName = (id?: string | number | null) =>
  id === null || id === undefined ? "" : CAT_MAP.get(String(id))?.name ?? "";

/** Full path, department included — there are three of them now. */
const catPathLabel = (id?: string | number | null) =>
  chainIdsOf(id).map((cid) => catName(cid)).join(" › ");

/** The three departments, in DEPARTMENTS order — the oval's toggles. */
const FOOD_BRANCHES: Record<string, string> = {
  fastfood: "0801",
  grocery:  "0802",
  farm:     "0803",
};
const FOOD_BRANCH_IDS = Object.values(FOOD_BRANCHES);
const FRESH_DEPARTMENTS: CategoryItem[] = FOOD_BRANCH_IDS
  .map((id) => CAT_MAP.get(id))
  .filter(Boolean) as CategoryItem[];

/** Does this category sit under Farm, Food or Grocery? */
const isFresh = (id?: string | number | null) =>
  chainIdsOf(id).some((cid) => FRESH_DEPT_IDS.includes(cid));

/* deterministic accent per department */
const DEPT_ACCENTS = ["#7A9E3B", "#3F8A4C", "#D4900F"]; // Farm · Food · Grocery
const deptAccent = (deptId: string) => {
  const idx = FRESH_DEPARTMENTS.findIndex((d) => d.id === deptId);
  return DEPT_ACCENTS[idx >= 0 ? idx % DEPT_ACCENTS.length : 0];
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — resolved exactly the way the seller screen
   resolves them, so the defs line up with the saved keys.
   Nothing about the fields is defined in this file.
═══════════════════════════════════════════════════════════ */
const toAttributeCategoryId = (id?: string | number | null): string => {
  if (id === null || id === undefined || id === "") return "";
  const chain = chainIdsOf(id).map((cid) => CAT_MAP.get(cid)!).filter(Boolean);
  const anchor = [...chain]
    .reverse()
    .find((c) => c.level === "category" || c.level === "department");
  return anchor ? `0${anchor.id}` : "";
};

const attrCache = new Map<string, any[]>();
const resolveAttributes = (categoryId?: string | number | null): any[] => {
  if (categoryId === null || categoryId === undefined || categoryId === "") return [];
  const cacheKey = String(categoryId);
  const hit = attrCache.get(cacheKey);
  if (hit) return hit;

  const chain = [...chainIdsOf(categoryId)].reverse();
  const keys = [
    toAttributeCategoryId(categoryId),
    ...chain.flatMap((cid) => [`0${cid}`, cid]),
  ].filter(Boolean);

  let found: any[] = [];
  for (const key of keys) {
    try {
      const defs = attributesForCategory(key as any);
      if (defs && defs.length) { found = defs; break; }
    } catch {
      /* unknown key — keep walking up */
    }
  }
  attrCache.set(cacheKey, found);
  return found;
};

/** Saved value (option id, array of ids, or free text) → readable label. */
const resolveAttrValue = (attr: any, value: any): string => {
  if (value === undefined || value === null || value === "") return "";
  const label = (optId: any) => {
    const opt = attr.options?.find((o: any) => String(o.id) === String(optId));
    return opt ? (opt.name || opt.standard || String(optId)) : String(optId);
  };
  if (Array.isArray(value)) return value.map(label).join(", ");
  if (attr.options?.length) return label(value);
  return `${value}${attr.unit ? ` ${attr.unit}` : ""}`;
};

const isEmptyValue = (v: any) =>
  v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);

/**
 * Only the attributes the grower actually filled in. An attribute the
 * seller left blank does not exist as far as this page is concerned —
 * nothing is invented, nothing is offered as a choice.
 */
const savedAttrsOf = (item: any) => {
  const saved = item?.attributes ?? {};
  return resolveAttributes(item?.categoryId)
    .map((def: any) => ({ def, raw: saved[String(def.id)] }))
    .filter(({ raw }) => !isEmptyValue(raw));
};

/** Saved colour options resolved to their swatches. Empty unless one was saved. */
const savedColorsOf = (item: any) =>
  savedAttrsOf(item)
    .filter(({ def }) => def.inputType === "color")
    .map(({ def, raw }) => {
      const ids = Array.isArray(raw) ? raw : [raw];
      return {
        id: String(def.id),
        name: def.name,
        swatches: ids
          .map((optId: any) => def.options?.find((o: any) => String(o.id) === String(optId)))
          .filter(Boolean)
          .map((o: any) => ({ id: String(o.id), name: o.name || o.standard || String(o.id), hex: o.hex })),
      };
    })
    .filter((c) => c.swatches.length > 0);

/* ═══════════════════════════════════════════════════════════
   SIZES / EXTRAS — FreshStore now writes `portions` as an array
   of {id,name,price,unit,stock}; older docs used a keyed map.
   Both are accepted so nothing in the wild breaks.
═══════════════════════════════════════════════════════════ */
export interface Portion { id: string; name: string; rate: number; unit?: string; stock?: number }
export interface Extra { id: string; name: string; rate: number }

const portionsOf = (item: any): Portion[] => {
  const raw = item?.portions;
  if (!raw) return [];
  const list: Portion[] = Array.isArray(raw)
    ? raw.map((p: any) => ({
        id: String(p?.id ?? p?.name ?? ""),
        name: p?.name ?? String(p?.id ?? ""),
        rate: Number(p?.rate ?? p?.price) || 0,
        unit: p?.unit ?? "",
        stock: Number(p?.stock) || 0,
      }))
    : Object.entries(raw).map(([k, v]: [string, any]) => ({
        id: k,
        name: v?.name ?? k,
        rate: Number(v?.rate ?? v?.price) || 0,
        unit: v?.unit ?? "",
        stock: Number(v?.stock) || 0,
      }));
  return list.filter((p) => p.id && p.name);
};

const extrasOf = (item: any): Extra[] =>
  Array.isArray(item?.extras)
    ? item.extras
        .map((e: any) => ({ id: String(e?.id ?? e?.name ?? ""), name: e?.name ?? "", rate: Number(e?.rate ?? e?.price) || 0 }))
        .filter((e: Extra) => e.id && e.name)
    : [];

const buildCartId = (item: any, sizeId: string | null, extraIds: string[]) => {
  if (!sizeId && extraIds.length === 0) return item.listingId;
  const extraPart = extraIds.length ? extraIds.slice().sort().join("+") : "none";
  return `${item.listingId}__${sizeId ?? "base"}__${extraPart}`;
};

const computeItemPrice = (item: any, sizeId: string | null, extraIds: string[]) => {
  const size = sizeId ? portionsOf(item).find((p) => p.id === sizeId) : null;
  const base = size ? size.rate : (item?.rate ?? 0); // no size picked → default rate; size picked → size IS the price
  const extras = extrasOf(item);
  const extrasSum = extraIds.reduce((sum, id) => sum + (extras.find((e) => e.id === id)?.rate || 0), 0);
  return base + extrasSum;
};

const buildCartLabel = (item: any, sizeId: string | null, extraIds: string[]) => {
  const size = portionsOf(item).find((p) => p.id === sizeId);
  const extras = extrasOf(item);
  let label = item?.name || "";
  if (size) label += ` (${size.name}${size.unit ? ` · ${size.unit}` : ""})`;
  const extraNames = extraIds.map((id) => extras.find((e) => e.id === id)?.name).filter(Boolean) as string[];
  if (extraNames.length) label += ` + ${extraNames.join(", ")}`;
  return label;
};

/* ═══════════════════════════════════════════════════════════
   FRESH HELPERS — how it's measured, how long it's got,
   how it's kept, and who can eat it.
═══════════════════════════════════════════════════════════ */
const measureLabel = (item: any) => {
  const n = item?.netContent;
  const u = item?.unit;
  if (n && u) return `${n} ${u}`;
  if (u) return `per ${u}`;
  return "";
};

const daysLeft = (bestBefore?: string): number | null => {
  if (!bestBefore) return null;
  const t = Date.parse(bestBefore);
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86_400_000);
};

const freshnessChip = (item: any): { text: string; color: string } | null => {
  const d = daysLeft(item?.bestBefore);
  if (d === null) {
    const shelf = item?.shelfLifeDays;
    return shelf ? { text: `${shelf}-day shelf life`, color: HEART.green } : null;
  }
  if (d <= 0) return { text: "Use today", color: HEART.red };
  if (d === 1) return { text: "1 day left", color: HEART.red };
  if (d <= 3) return { text: `${d} days left`, color: HEART.goldDark };
  return { text: `Good for ${d} days`, color: HEART.green };
};

const STORAGE_META: Record<string, { label: string; icon: any }> = {
  frozen: { label: "Keep frozen", icon: Snowflake },
  chilled: { label: "Keep chilled", icon: Snowflake },
  ambient: { label: "Room temp", icon: Sun },
  dry: { label: "Keep dry", icon: Sun },
};

const SPICE = ["None", "Mild", "Medium", "Hot"];

const isOutOfStock = (item: any) =>
  item?.available === false || (item?.stock ?? 0) <= 0;

type SortKey = "default" | "freshest" | "price_asc" | "price_desc" | "name_asc";

/* ═══════════════════════════════════════════════════════════
   LOCATION TIERS
═══════════════════════════════════════════════════════════ */
const COUNTRY_TO_CONTINENT: Record<string, string> = {
  estonia: "Europe", latvia: "Europe", lithuania: "Europe", finland: "Europe",
  sweden: "Europe", norway: "Europe", denmark: "Europe", germany: "Europe",
  france: "Europe", spain: "Europe", italy: "Europe", portugal: "Europe",
  poland: "Europe", "united kingdom": "Europe", ireland: "Europe",
  netherlands: "Europe", belgium: "Europe", austria: "Europe",
  switzerland: "Europe", greece: "Europe",
  "united states": "North America", canada: "North America", mexico: "North America",
  brazil: "South America", argentina: "South America",
  nigeria: "Africa", kenya: "Africa", "south africa": "Africa",
  china: "Asia", japan: "Asia", india: "Asia", "united arab emirates": "Asia",
  australia: "Oceania", "new zealand": "Oceania",
};
const getContinent = (country?: string | null): string | null => {
  if (!country) return null;
  return COUNTRY_TO_CONTINENT[country.toLowerCase().trim()] ?? null;
};
const isLocalTier = (tier: number) => tier <= 1;
const isGlobalTier = (tier: number) => tier >= 2;

/* ═══════════════════════════════════════════════════════════
   GLOBAL CSS — StoreProductsHome's, renamed to fsh- so both
   pages can be mounted without clashing.
═══════════════════════════════════════════════════════════ */
const GLOBAL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800;900&family=Archivo+Black&display=swap');
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
::-webkit-scrollbar { display: none; }
input, button, select { font-family: inherit; }
input::placeholder { color: rgba(255,255,255,.55); }

@keyframes gradSpin { 0%{background-position:0% 50%;} 50%{background-position:100% 50%;} 100%{background-position:0% 50%;} }
@keyframes titleGlow { 0%,100%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 14px 30px rgba(200,32,46,.18);} 50%{ text-shadow: 0 2px 0 rgba(27,14,6,.08), 0 18px 40px rgba(200,32,46,.30);} }
@keyframes fshPulse { 0%,100%{opacity:1} 50%{opacity:.35} }
@keyframes ovalSheen { 0%{ transform: translateX(-160%) skewX(-18deg);} 60%,100%{ transform: translateX(320%) skewX(-18deg);} }

.p-card { transition: box-shadow .22s, transform .22s; }
.p-card:hover { box-shadow: 0 4px 10px rgba(0,0,0,.06), 0 24px 48px rgba(0,0,0,.16); transform: translateY(-6px); }
.fsh-plus { transition: transform .15s; }
.fsh-plus:hover { transform: scale(1.08); }
.fsh-plus:active { transform: scale(.92); }

.tab-item { display:flex; flex-direction:column; align-items:center; gap:3px; font-size:10px; font-weight:700; cursor:pointer; color:${T.mutedDim}; padding:4px 10px; transition:color .2s; }
.tab-item.active { color:${T.red}; }

.fsh-title {
  font-family: 'Archivo Black','Montserrat',sans-serif;
  font-weight: 900; text-transform: uppercase;
  letter-spacing: -0.02em; line-height: 0.95;
  background: linear-gradient(180deg, ${HEART.dark} 0%, ${HEART.dark} 60%, ${HEART.red} 130%);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  animation: titleGlow 3.4s ease-in-out infinite;
}

.fsh-hbtn{display:flex;align-items:center;gap:6px;background:rgba(255,255,255,.16);
  border:1px solid rgba(255,255,255,.3);cursor:pointer;color:#fff;
  font-size:12px;font-weight:700;padding:9px 14px;border-radius:999px;
  flex-shrink:0;white-space:nowrap;transition:background .2s;font-family:inherit;}
.fsh-hbtn:hover{background:rgba(255,255,255,.26);}

/* ✨ THE OVAL — one glassy capsule holding every food category */
.fsh-oval {
  position: relative; display: flex; align-items: center; gap: 4px;
  padding: 7px 10px; border-radius: 999px;
  background:
    linear-gradient(#FFFDF8, #FFF6E6) padding-box,
    linear-gradient(120deg, ${HEART.gold}, ${HEART.red} 45%, ${HEART.green} 92%) border-box;
  border: 2px solid transparent;
  box-shadow: 0 10px 30px rgba(200,32,46,.14), 0 2px 6px rgba(27,14,6,.08), inset 0 1px 0 rgba(255,255,255,.9);
  overflow-x: auto; overflow-y: hidden; scrollbar-width: none; -webkit-overflow-scrolling: touch;
}
.fsh-oval::after {
  content: ""; position: absolute; top: 0; bottom: 0; left: 0; width: 90px;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,.75), transparent);
  animation: ovalSheen 5.5s ease-in-out infinite; pointer-events: none;
}
.fsh-oval-toggle {
  position: relative; flex-shrink: 0; display: flex; align-items: center; gap: 7px;
  border: none; cursor: pointer; border-radius: 999px; padding: 9px 16px;
  font-size: 12px; font-weight: 800; letter-spacing: .03em;
  color: ${HEART.textMuted}; background: transparent; white-space: nowrap;
  transition: color .18s, transform .18s;
}
.fsh-oval-toggle:hover { color: ${HEART.dark}; transform: translateY(-1px); }
.fsh-oval-toggle .fsh-ic { display: flex; font-size: 13px; transition: transform .18s; }
.fsh-oval-toggle:hover .fsh-ic { transform: rotate(-8deg) scale(1.12); }
.fsh-oval-toggle.active {
  color: #fff; background: linear-gradient(135deg, ${HEART.red}, ${HEART.redDark});
  box-shadow: 0 8px 18px rgba(200,32,46,.42), inset 0 1px 0 rgba(255,255,255,.28);
}
.fsh-oval-toggle.active .fsh-ic { transform: none; }

.fsh-circle { transition: transform .22s cubic-bezier(.34,1.56,.64,1); }
.fsh-circle:hover { transform: translateY(-3px) scale(1.05); }
.fsh-tile { background:none;border:none;cursor:pointer;padding:0;border-radius:14px;overflow:hidden; }
.fsh-opt { transition: border-color .15s, background .15s; }
.fsh-opt:hover { border-color: ${RGBA_RED(0.45)}; }

@media (prefers-reduced-motion: reduce){
  .fsh-oval::after{animation:none;}
  .fsh-title{animation:none;}
  .fsh-circle,.fsh-plus{transition:none;}
}
`;

const A: Record<string, React.CSSProperties> = {
  commerceCard: { borderRadius: 18, padding: 18, background: "#fff", marginBottom: 16, boxShadow: "0 2px 16px rgba(27,14,6,.06)", border: T.outline },
  commerceHeader: { fontSize: 16, fontWeight: 900, display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", color: T.black },
  category: { padding: 10, borderRadius: 12, background: HEART.bg, marginTop: 8, border: `1px solid ${HEART.border}`, cursor: "pointer" },
  row: { display: "flex", alignItems: "center", gap: 10, fontWeight: 700, color: T.black },
  sub: { paddingLeft: 20, paddingTop: 8 },
  sidebar: { background: "rgba(255,255,255,.98)", borderRadius: 20, padding: 20, boxShadow: "0 4px 24px rgba(27,14,6,.08)", position: "sticky" as const, top: 116, maxHeight: "calc(100vh - 130px)", overflowY: "auto" as const, border: T.outline },
};

const S: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", background: T.pageBg, paddingBottom: 90, fontFamily: "'Montserrat',sans-serif", position: "relative" as const },
  content: { position: "relative" as const, zIndex: 1 },
  headerTop: { background: GRAD_H, backgroundSize: "200% 100%", position: "sticky", top: 0, zIndex: 200, boxShadow: `0 4px 24px ${RGBA_RED(0.25)}` },
  searchWrap: { display: "flex", alignItems: "center", flex: 1, background: "rgba(255,255,255,.18)", borderRadius: 999, overflow: "hidden", border: "1.5px solid rgba(255,255,255,.32)" },
  searchInput: { flex: 1, border: "none", outline: "none", padding: "10px 14px", fontSize: 13, color: "#fff", background: "transparent", fontFamily: "inherit" },
  searchBtn: { width: 42, minHeight: 40, background: "rgba(255,255,255,.16)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, borderRadius: "0 999px 999px 0" },
  desktopGrid: { display: "grid", gap: 24, padding: "24px 24px 0" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 20 },
  mobileGrid: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 12 },
  mobileTabs: { position: "fixed" as const, bottom: 0, left: 0, right: 0, height: 64, background: T.cardBg, display: "flex", justifyContent: "space-around", alignItems: "center", borderTop: `1px solid ${HEART.border}`, zIndex: 100 },
};

const PageWatermark: React.FC = () => (
  <div
    aria-hidden="true"
    style={{
      position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
      backgroundImage: `url(${appLogo})`, backgroundRepeat: "repeat",
      backgroundSize: "170px 170px", opacity: 0.045, filter: "grayscale(1)",
    }}
  />
);

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
const FreshStoreHome: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { rate, symbol } = useContext(CurrencyContext);
  const cur = symbol ?? "€";
  const fx = (n: number) => (n ?? 0) * (rate ?? 1);
  const price = (n: number) => `${cur}${fx(n).toFixed(2)}`;

  const authUser = useSelector((s: RootState) => s.auth.user);
  const authLoaded = useSelector((s: RootState) => s.auth.authLoaded);
  const userId = authUser?.userId ?? "";

  const listings = useSelector((s: RootState) => (s as any).food.listings as FoodItem[]);
  const filteredListings = useSelector((s: RootState) => (s as any).food.filteredListings as FoodItem[]);
  const cartItems = useSelector((s: RootState) => s.cart.items);
  const ratingSummaries = useSelector((s: RootState) => (s as any).productRatings?.summaries ?? {});
  const commercePartners = useSelector((s: RootState) => s.commercePartners.partners);

  const cartTotal = cartItems.reduce((s: number, i: any) => s + (i.qty ?? 1), 0);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [catOpen, setCatOpen] = useState(false);
  const [desktopCatOpen, setDesktopCatOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSort, setActiveSort] = useState<SortKey>("default");

  /* the food category is the master toggle */
  const [activeDept, setActiveDept] = useState<string>(
    FRESH_DEPARTMENTS.some((d) => d.id === DEFAULT_DEPT_ID)
      ? DEFAULT_DEPT_ID
      : FRESH_DEPARTMENTS[0]?.id ?? DEFAULT_DEPT_ID
  );
  const dept = useMemo(
    () => FRESH_DEPARTMENTS.find((c) => c.id === activeDept) ?? FRESH_DEPARTMENTS[0],
    [activeDept]
  );

  /* deeper selections inside the active category */
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [activeDeep, setActiveDeep] = useState<string | null>(null);

  const [openDeptPanel, setOpenDeptPanel] = useState<string | null>(
    FRESH_DEPARTMENTS.some((d) => d.id === DEFAULT_DEPT_ID)
      ? DEFAULT_DEPT_ID
      : FRESH_DEPARTMENTS[0]?.id ?? null
  );
  const [openNodes, setOpenNodes] = useState<Set<string>>(new Set());
  const [shopCatExpanded, setShopCatExpanded] = useState(true);

  const [userCountry, setUserCountry] = useState<string | null>(null);
  const [userCity, setUserCity] = useState<string | null>(null);
  const [userLat, setUserLat] = useState<number | null>(null);
  const [locationDenied, setLocationDenied] = useState(false);
  const [locationLoading, setLocationLoading] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [showGlobal, setShowGlobal] = useState(false);
  const locationActive = !locationDenied && userLat !== null;

  /* detail modal state */
  const [detailItem, setDetailItem] = useState<any | null>(null);
  const [imgIndex, setImgIndex] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedExtras, setSelectedExtras] = useState<string[]>([]);

  const brandMap = useMemo(() => {
    const m = new Map<string, any>();
    (commercePartners || []).forEach((p: any) => m.set(p.brandId, p));
    return m;
  }, [commercePartners]);

  const brandTier = useMemo(() => {
    const tiers = new Map<string, number>();
    const deviceCity = userCity?.toLowerCase().trim();
    const deviceCountry = userCountry?.toLowerCase().trim();
    const deviceContinent = getContinent(userCountry);
    (commercePartners || []).forEach((p: any) => {
      const brandCity = p.address?.city?.toLowerCase().trim();
      const brandCountry = p.address?.country?.toLowerCase().trim();
      const brandContinent = getContinent(p.address?.country);
      let tier = 3;
      if (deviceCity && brandCity && brandCity === deviceCity) tier = 0;
      else if (deviceCountry && brandCountry && brandCountry === deviceCountry) tier = 1;
      else if (deviceContinent && brandContinent && brandContinent === deviceContinent) tier = 2;
      tiers.set(p.brandId, tier);
    });
    return tiers;
  }, [commercePartners, userCity, userCountry]);

  /* ── responsive ── */
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth <= 768);
    fn(); window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* ── auth gate ── */
  useEffect(() => {
    if (!authLoaded) return;
    if (!authUser || !userId) navigate("/AuthSwitch");
  }, [authLoaded, authUser, userId, navigate]);

  /* ── data listeners ── */
  useEffect(() => { dispatch(listenToPublishedFoods() as any); }, [dispatch]);
  useEffect(() => { dispatch(fetchAllProductSummaries() as any); }, [dispatch]);
  useEffect(() => {
    const unsub = dispatch(listenToBrandsByBusinessType(FRESH_BUSINESS_TYPE) as any);
    return () => { if (typeof unsub === "function") unsub(); };
  }, [dispatch]);
  useEffect(() => { dispatch(filterLocalFoodsByCountry(null) as any); }, [dispatch]);

  /* ── geolocation ── */
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationDenied(true); setLocationLoading(false);
      dispatch(filterLocalFoodsByCountry(null) as any);
      return;
    }
    const detect = () => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserLat(pos.coords.latitude); setLocationDenied(false);
          if ((window as any).google?.maps) {
            const gc = new (window as any).google.maps.Geocoder();
            gc.geocode({ location: { lat: pos.coords.latitude, lng: pos.coords.longitude } }, (results: any, status: string) => {
              if (status === "OK" && results?.[0]) {
                const comps = results[0].address_components;
                const get = (t: string) => comps.find((c: any) => c.types.includes(t))?.long_name ?? null;
                setUserCity(get("locality") || get("administrative_area_level_2"));
                const country = get("country"); setUserCountry(country);
                if (country) dispatch(filterLocalFoodsByCountry(country) as any);
              }
              setLocationLoading(false);
            });
          } else { setLocationLoading(false); }
        },
        (err) => {
          if (err.code === err.PERMISSION_DENIED) setLocationDenied(true);
          setLocationLoading(false);
          dispatch(filterLocalFoodsByCountry(null) as any);
        }
      );
    };
    detect();
    if ("permissions" in navigator) {
      navigator.permissions.query({ name: "geolocation" as PermissionName }).then((r) => {
        r.onchange = () => { if (r.state === "granted") detect(); };
      }).catch(() => {});
    }
  }, [dispatch]);

  useEffect(() => {
    if (!userCountry) return;
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  }, [userCountry, showGlobal, dispatch]);

  /* ── actions ── */
  const closePanel = () => { setCatOpen(false); setDesktopCatOpen(false); };

  const handleToggleGlobal = () => {
    const next = !showGlobal; setShowGlobal(next);
    setActiveCat(null); setActiveDeep(null);
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(next ? null : userCountry) as any);
  };

  const resetAllFilters = () => {
    setActiveCat(null); setActiveDeep(null); setActiveSort("default"); setSearchQuery("");
    dispatch(setFilters({ city: null, country: null }));
    dispatch(filterLocalFoodsByCountry(showGlobal ? null : userCountry) as any);
  };

  const handleDeptSelect = (catId: string) => {
    setActiveDept(catId);
    setActiveCat(null); setActiveDeep(null);
    setOpenDeptPanel(catId);
    setOpenNodes(new Set());
  };
  const handleCatSelect = (id: string) => { setActiveCat((p) => (p === id ? null : id)); setActiveDeep(null); };
  const handleDeepSelect = (id: string) => setActiveDeep((p) => (p === id ? null : id));

  const toggleNode = (id: string) =>
    setOpenNodes((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const getQty = (id: string) => cartItems.find((i: any) => i.listingId === id)?.qty ?? 0;

  //const SINGLE_BRAND_CART_MODE = true;
  const SINGLE_BRAND_CART_MODE = false
  const ensureCartBrandCompat = (newBrandId?: string) => {
    if (!SINGLE_BRAND_CART_MODE || !cartItems.length) return;
    const currentBrandId = cartItems[0]?.brandId;
    if (currentBrandId && newBrandId && currentBrandId !== newBrandId) dispatch(clearCart());
  };

  /**
   * One place builds the cart line, so the shelf, the sheet and the till
   * never disagree about what's being bought or what it costs — same
   * contract as StoreProductsHome's buildCartLine.
   */
  const buildFoodCartLine = (item: any, sizeId: string | null, extraIds: string[], quantity: number) => {
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const unit = computeItemPrice(item, sizeId, extraIds);
    return {
      listingId: buildCartId(item, sizeId, extraIds),
      baseListingId: item.listingId,
      name: buildCartLabel(item, sizeId, extraIds),
      rate: unit,
      rateType: rt,
      qty: quantity,
      /* what this line comes to, by the same maths the fee slice runs */
      lineTotal: calculateLineTotal(unit, quantity, rt),
      photo: item.photos?.[0],
      hostId: item.hostId,
      brandId: item.brandId,
      unit: item.unit,
      netContent: item.netContent,
      portionId: sizeId ?? undefined,
      extraIds: extraIds.length ? extraIds : undefined,
      categoryId: item.categoryId ?? "",
      attributes: item.attributes ?? {},
    };
  };

  const addLine = (item: any, sizeId: string | null, extraIds: string[], count = 1) => {
    if (isOutOfStock(item)) return;
    ensureCartBrandCompat(item.brandId);
    dispatch(addToCart(buildFoodCartLine(item, sizeId, extraIds, Math.max(1, count)) as any));
  };

  const removeLine = (item: any, sizeId: string | null, extraIds: string[]) =>
    dispatch(decrementCartItem({ listingId: buildCartId(item, sizeId, extraIds) }));

  const goToCheckout = (extra?: any) =>
    navigate(`/Freshpay/${userId}`, { state: { items: extra ? [...cartItems, extra] : cartItems } });
  const goToBrand = (id: string) => { if (id && id !== "unknown") navigate(`/FreshStorebrand/${id}`); };

  const openDetail = (item: any) => {
    setDetailItem(item);
    setImgIndex(0);
    setQty(1);
    setSelectedExtras([]);
    setSelectedSize(null); // nothing pre-selected — default rate applies until a size is chosen
  };
  const closeDetail = () => setDetailItem(null);

  /* quick add from a card — always adds at the default rate, no forced detour to the sheet */
  const quickAdd = (item: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (isOutOfStock(item)) return;
    addLine(item, null, [], 1);
  };

  /* ═══════════════════════════════════
     FILTERING — everything resolved through the ancestry chain
     of the item's saved categoryId, and always inside Food.
  ═══════════════════════════════════ */
  const filteredProducts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let list: any[] = (filteredListings ?? []).filter((l: any) => {
      if (!l.isPublished) return false;
      /* out-of-stock stays on the shelf — the card grays itself out below */
      const chain = chainIdsOf(l.categoryId);
      if (!chain.some((cid) => FRESH_DEPT_IDS.includes(cid))) return false;
      if (!FOOD_BRANCH_IDS.some((b) => chain.includes(b))) return false;
      if (!chain.includes(activeDept)) return false;
      if (activeCat && !chain.includes(activeCat)) return false;
      if (activeDeep && !chain.includes(activeDeep)) return false;
      if (q) {
        const hay = `${l.name ?? ""} ${l.description ?? ""} ${l.category ?? ""} ${l.unit ?? ""} ${(l.allergens ?? []).join(" ")} ${(l.tags ?? []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });

    if (activeSort === "price_asc") list = [...list].sort((a, b) => (a.rate ?? 0) - (b.rate ?? 0));
    else if (activeSort === "price_desc") list = [...list].sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
    else if (activeSort === "name_asc") list = [...list].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
    else if (activeSort === "freshest")
      list = [...list].sort((a, b) => {
        const av = Date.parse(a.harvestedOn ?? a.bestBefore ?? "") || 0;
        const bv = Date.parse(b.harvestedOn ?? b.bestBefore ?? "") || 0;
        return bv - av;
      });

    return list;
  }, [filteredListings, activeDept, activeCat, activeDeep, searchQuery, activeSort]);

  const productsByBrand = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, any[]>();
    filteredProducts.forEach((item: any) => {
      const bId = item.brandId || item.hostId || "unknown";
      if (!map.has(bId)) { map.set(bId, []); order.push(bId); }
      map.get(bId)!.push(item);
    });
    let groups = order.map((bId) => ({ brandId: bId, items: map.get(bId)! }));

    if (locationActive) {
      groups = groups.filter((g) => {
        const tier = brandTier.get(g.brandId) ?? 3;
        return showGlobal ? isGlobalTier(tier) : isLocalTier(tier);
      });
    }
    return [...groups].sort((a, b) => (brandTier.get(a.brandId) ?? 3) - (brandTier.get(b.brandId) ?? 3));
  }, [filteredProducts, showGlobal, brandTier, locationActive]);

  const visibleCount = useMemo(
    () => productsByBrand.reduce((sum, g) => sum + g.items.length, 0),
    [productsByBrand]
  );

  const activeFilterName = activeDeep ? catName(activeDeep) : activeCat ? catName(activeCat) : null;
  const deptCategories = useMemo(() => childrenOf(activeDept), [activeDept]);

  /* freshest picks — harvested most recently, inside the active category */
  const freshestPicks = useMemo(() => {
    return [...(listings ?? [])]
      .filter((l: any) => l.isPublished && !isOutOfStock(l) && isFresh(l.categoryId) && chainIdsOf(l.categoryId).includes(activeDept))
      .sort((a: any, b: any) => {
        const av = Date.parse(a.harvestedOn ?? "") || 0;
        const bv = Date.parse(b.harvestedOn ?? "") || 0;
        if (av !== bv) return bv - av;
        return (ratingSummaries[b.listingId]?.avgRating ?? 0) - (ratingSummaries[a.listingId]?.avgRating ?? 0);
      })
      .slice(0, 16);
  }, [listings, activeDept, ratingSummaries]);

  /* ═══════════════════════════════════
     HEADER
     (plain function calls, not <Component />, so the search
     input keeps focus between keystrokes)
  ═══════════════════════════════════ */
  const headerRow = () => (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: isMobile ? "12px 14px" : "14px 20px" }}>
      {locationLoading ? (
        <div className="fsh-hbtn">
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#fff", animation: "fshPulse 1.2s infinite" }} />
          <span style={{ display: isMobile ? "none" : "inline" }}>Finding you…</span>
        </div>
      ) : locationDenied ? (
        <button className="fsh-hbtn" onClick={() => setLocationModalOpen(true)} aria-label="Location off">
          <AlertTriangle size={14} />
        </button>
      ) : (
        <button onClick={handleToggleGlobal} className="fsh-hbtn">
          {showGlobal ? <Globe size={13} /> : <MapPin size={13} />}
          <span style={{ display: isMobile ? "none" : "inline" }}>{showGlobal ? "Global" : userCity || userCountry || "Nearby"}</span>
        </button>
      )}

      <div style={S.searchWrap}>
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search produce or growers…"
          style={S.searchInput}
          aria-label="Search produce"
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery("")} aria-label="Clear search"
            style={{ background: "none", border: "none", cursor: "pointer", padding: "0 8px", color: "#fff" }}>
            <X size={13} />
          </button>
        )}
        <button style={S.searchBtn} aria-label="Search"><Search size={16} color="#fff" /></button>
      </div>

      <button onClick={() => (isMobile ? setCatOpen(true) : setDesktopCatOpen((v) => !v))} className="fsh-hbtn">
        <LayoutGrid size={14} />
        <span style={{ display: isMobile ? "none" : "inline" }}>Categories</span>
      </button>

      <button onClick={() => goToCheckout()} className="fsh-hbtn" style={{ position: "relative" }}>
        <ShoppingCart size={16} />
        {!isMobile && <span>{cartTotal > 0 ? `${cartTotal} items` : "Basket"}</span>}
        {cartTotal > 0 && (
          <span style={{ position: "absolute", top: -6, right: -6, background: HEART.redDark, color: "#fff", fontSize: 9, fontWeight: 900, width: 17, height: 17, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {cartTotal > 99 ? "99+" : cartTotal}
          </span>
        )}
      </button>
    </div>
  );

  /* ═══════════════════════════════════
     🎬 TITLE + ✨ OVAL TOGGLE BAR (food categories)
  ═══════════════════════════════════ */
  const titleBanner = () => (
    <div style={{ padding: isMobile ? "6px 4px 4px" : "4px 4px 8px", display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
      <span className="fsh-title" style={{ fontSize: isMobile ? 30 : 46 }}>{dept?.name ?? "Fresh"}</span>
      <span style={{ fontSize: isMobile ? 11 : 13, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim, textTransform: "uppercase" }}>
        {locationDenied ? "Worldwide" : showGlobal ? "Global" : userCity ? `Grown near ${userCity}` : "Near you"}
      </span>
    </div>
  );

  const ovalBar = () => (
    <div style={{ padding: isMobile ? "0 4px 14px" : "0 4px 18px" }}>
      <div className="fsh-oval">
        <span style={{ display: "flex", alignItems: "center", gap: 5, flexShrink: 0, padding: "0 6px 0 8px", color: HEART.green }}>
          <Sparkles size={14} />
        </span>
        {FRESH_DEPARTMENTS.map((c) => {
          const isActive = c.id === activeDept;
          return (
            <button key={c.id} className={`fsh-oval-toggle${isActive ? " active" : ""}`} onClick={() => handleDeptSelect(c.id)}>
              <span className="fsh-ic" style={{ color: isActive ? "#fff" : deptAccent(c.id) }}>{c.icon}</span>
              {c.name}
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ═══════════════════════════════════
     SIDEBAR TREE — drills the active food category downwards
  ═══════════════════════════════════ */
  const TreeNode: React.FC<{ node: CategoryItem; depth: number }> = ({ node, depth }) => {
    const kids = childrenOf(node.id);
    const isOpen = openNodes.has(node.id);
    const isPicked = activeCat === node.id || activeDeep === node.id;

    return (
      <div style={depth === 0 ? A.category : { paddingLeft: 14, paddingTop: 7 }}>
        <div
          style={{ ...A.row, fontSize: depth === 0 ? 14 : 13, color: isPicked ? T.red : T.black, cursor: "pointer" }}
          onClick={() => {
            if (depth === 0) handleCatSelect(node.id);
            else handleDeepSelect(node.id);
            if (kids.length) toggleNode(node.id);
            else closePanel();
          }}
        >
          <span style={{ display: "flex", color: isPicked ? T.red : deptAccent(activeDept) }}>{node.icon}</span>
          {node.name}
          {kids.length > 0 && (
            <motion.div animate={{ rotate: isOpen ? 90 : 0 }} style={{ marginLeft: "auto" }}>
              <ChevronRight size={13} color={T.mutedDim} />
            </motion.div>
          )}
          {isPicked && kids.length === 0 && <Check size={13} color={T.red} style={{ marginLeft: "auto" }} />}
        </div>
        <AnimatePresence>
          {isOpen && kids.length > 0 && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              <div style={A.sub}>
                {kids.map((k) => <TreeNode key={k.id} node={k} depth={depth + 1} />)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const deptTree = () => {
    const isOpen = openDeptPanel === activeDept;
    return (
      <div style={A.commerceCard}>
        <div style={A.commerceHeader} onClick={() => setOpenDeptPanel(isOpen ? null : activeDept)}>
          {dept?.name}
          <motion.div animate={{ rotate: isOpen ? 90 : 0 }}><ChevronRight size={16} /></motion.div>
        </div>
        <AnimatePresence>
          {isOpen && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: "hidden" }}>
              {deptCategories.length === 0 ? (
                <p style={{ fontSize: 12, color: T.mutedDim, paddingTop: 10 }}>No sub-categories here yet.</p>
              ) : (
                deptCategories.map((c) => <TreeNode key={c.id} node={c} depth={0} />)
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  const mobileCategorySheet = () => (
    <AnimatePresence>
      {isMobile && catOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCatOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.55)", zIndex: 400 }} />
          <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
            style={{ position: "fixed", bottom: 0, left: 0, right: 0, maxHeight: "82vh", borderRadius: "22px 22px 0 0", background: "#fff", zIndex: 401, border: T.outline, overflowY: "auto" }}>
            <div style={{ width: 36, height: 4, background: HEART.border, borderRadius: 2, margin: "14px auto 0" }} />
            <div style={{ display: "flex", alignItems: "center", borderBottom: `1px solid ${HEART.border}`, padding: "12px 20px 10px" }}>
              <span style={{ fontWeight: 700, fontSize: 11, letterSpacing: "0.15em", color: T.red }}>CATEGORIES</span>
              <div style={{ flex: 1 }} />
              <button onClick={() => setCatOpen(false)} aria-label="Close categories" style={{ background: "none", border: "none", cursor: "pointer" }}>
                <X size={15} color={T.muted} />
              </button>
            </div>
            <div style={{ padding: "16px 16px 32px" }}>
              {ovalBar()}
              {deptTree()}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  /* ═══════════════════════════════════
     SUB-CATEGORY ICON RAIL
  ═══════════════════════════════════ */
  const categoryIconBar = () => {
    if (!deptCategories.length) return null;
    const accent = deptAccent(activeDept);
    return (
      <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: HEART.panelBg, borderRadius: 18, border: T.outline }}>
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {deptCategories.map((node) => {
            const isSel = activeCat === node.id;
            return (
              <button key={node.id} onClick={() => handleCatSelect(node.id)}
                style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
                <div className="fsh-circle" style={{
                  width: isMobile ? 46 : 54, height: isMobile ? 46 : 54, borderRadius: "50%", overflow: "hidden",
                  border: `2px solid ${isSel ? T.red : HEART.border}`,
                  boxShadow: isSel ? `0 0 0 3px ${T.redDim}` : "none", transition: "all .2s",
                }}>
                  {(node as any).image ? (
                    <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: isSel ? 1 : 0.85 }} />
                  ) : (
                    <div style={{ width: "100%", height: "100%", background: accent, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <span style={{ color: "#fff", display: "flex", fontSize: isMobile ? 18 : 22 }}>{node.icon}</span>
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isSel ? 800 : 600, color: isSel ? T.red : T.black, whiteSpace: "nowrap" }}>{node.name}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     FRESHEST PICKS RAIL
  ═══════════════════════════════════ */
  const freshestBar = () => (
    <div style={{ marginBottom: 22, padding: isMobile ? "14px 16px" : "18px 24px", background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 6s ease infinite", borderRadius: 18, boxShadow: `0 8px 24px ${RGBA_RED(0.18)}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <Sprout size={14} color="#fff" />
        <span style={{ fontWeight: 800, fontSize: 12, color: "#fff", letterSpacing: ".04em" }}>Picked most recently</span>
      </div>
      {freshestPicks.length === 0 ? (
        <p style={{ color: "rgba(255,255,255,.85)", fontSize: 12, fontWeight: 600 }}>
          Today's harvest lands here as soon as growers publish it.
        </p>
      ) : (
        <div style={{ display: "flex", gap: isMobile ? 14 : 22, overflowX: "auto", scrollbarWidth: "none" as any, WebkitOverflowScrolling: "touch" as any, paddingBottom: 2 }}>
          {freshestPicks.map((item: any) => (
            <button key={item.listingId} className="fsh-circle" onClick={() => openDetail(item)}
              style={{ background: "none", border: "none", cursor: "pointer", flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 6, padding: "2px 4px" }}>
              <div style={{
                width: isMobile ? 58 : 68, height: isMobile ? 58 : 68, borderRadius: "50%", overflow: "hidden",
                border: "2px solid rgba(255,255,255,.5)", background: HEART.goldSoft, position: "relative", boxShadow: "0 6px 16px rgba(0,0,0,.18)",
              }}>
                {item.photos?.[0] ? (
                  <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                ) : (
                  <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Sprout size={20} color={HEART.green} style={{ opacity: 0.6 }} />
                  </div>
                )}
                {item.isOrganic && (
                  <div style={{ position: "absolute", bottom: -2, right: -2, width: 18, height: 18, borderRadius: "50%", background: HEART.green, border: "1.5px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Leaf size={9} color="#fff" />
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", whiteSpace: "nowrap", maxWidth: 74, overflow: "hidden", textOverflow: "ellipsis" }}>
                {item.name || "Produce"}
              </span>
              <span style={{ fontSize: 10, fontWeight: 900, color: "#fff", background: "rgba(0,0,0,.22)", padding: "1px 8px", borderRadius: 999 }}>
                {price(item.rate ?? 0)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  /* ═══════════════════════════════════
     SHOP BY CATEGORY — tiles select, never navigate
  ═══════════════════════════════════ */
  const shopByCategory = () => {
    if (!deptCategories.length) return null;

    const focusId = activeCat ?? deptCategories[0]?.id ?? activeDept;
    const tileSource = childrenOf(focusId);
    if (!tileSource.length) return null;

    const chunks: CategoryItem[][] = [];
    for (let i = 0; i < tileSource.length; i += 4) chunks.push(tileSource.slice(i, i + 4));
    const focusName = focusId === activeDept ? dept?.name : catName(focusId);
    const cardGroups = chunks.map((tiles, idx) => ({
      title: chunks.length > 1 ? `${focusName} ${idx + 1}` : (focusName || "All"),
      tiles,
    }));

    return (
      <div style={{ marginBottom: 28 }}>
        <div onClick={() => setShopCatExpanded((v) => !v)}
          style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none", marginBottom: 14 }}>
          <span style={{ fontWeight: 900, fontSize: isMobile ? 16 : 18, color: T.black, letterSpacing: -0.3 }}>Shop by category</span>
          <motion.div animate={{ rotate: shopCatExpanded ? 0 : -90 }} transition={{ duration: 0.2 }}>
            <ChevronDown size={20} color={T.muted} />
          </motion.div>
        </div>

        <AnimatePresence initial={false}>
          {shopCatExpanded && (
            <motion.div key={`${activeDept}-${focusId}`} initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} style={{ overflow: "hidden" }}>
              <div style={isMobile
                ? { display: "flex", gap: 14, overflowX: "auto", scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" as any, scrollbarWidth: "none" as any, paddingBottom: 4 }
                : { display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(220px,1fr))", gap: 18 }
              }>
                {cardGroups.map((group, gi) => (
                  <div key={`${group.title}-${gi}`}
                    style={{ flexShrink: 0, ...(isMobile ? { width: "78vw", scrollSnapAlign: "start" } : {}), background: "#fff", borderRadius: 20, overflow: "hidden", border: T.outline, boxShadow: "0 6px 28px rgba(27,14,6,.06)", transition: "box-shadow .22s, border-color .22s, transform .22s" }}
                    onMouseEnter={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = `0 8px 0 ${T.redDim}, 0 16px 38px rgba(27,14,6,.16)`; el.style.borderColor = T.red; el.style.transform = "translateY(-4px)"; }}
                    onMouseLeave={(e) => { const el = e.currentTarget as HTMLElement; el.style.boxShadow = "0 6px 28px rgba(27,14,6,.06)"; el.style.borderColor = HEART.border; el.style.transform = "translateY(0)"; }}
                  >
                    <div style={{ padding: isMobile ? "10px 12px 6px" : "14px 16px 8px", fontWeight: 900, fontSize: isMobile ? 12 : 14, color: T.black, letterSpacing: -0.2 }}>
                      {group.title}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? 4 : 8, padding: isMobile ? "0 8px 8px" : "0 10px 10px" }}>
                      {group.tiles.map((node) => {
                        const sel = activeDeep === node.id;
                        const hasKids = childrenOf(node.id).length > 0;
                        return (
                          <button key={node.id} className="fsh-tile" onClick={() => handleDeepSelect(node.id)}>
                            <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: isMobile ? 10 : 14, overflow: "hidden", position: "relative", background: HEART.bg, outline: sel ? `2.5px solid ${T.red}` : "none", outlineOffset: 2, transition: "outline .15s" }}>
                              {(node as any).image ? (
                                <>
                                  <img src={(node as any).image} alt={node.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                  <div style={{ position: "absolute", inset: 0, background: `linear-gradient(to top, ${HEART.dark}EE, transparent 55%)` }} />
                                  <span style={{ position: "absolute", bottom: isMobile ? 5 : 7, left: 4, right: 4, textAlign: "center", fontSize: isMobile ? 9 : 11, fontWeight: 800, color: "#fff", lineHeight: 1.15, textShadow: "0 1px 4px rgba(0,0,0,.5)" }}>
                                    {node.name}
                                  </span>
                                </>
                              ) : (
                                <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: 4 }}>
                                  <span style={{ fontSize: isMobile ? 22 : 26, color: deptAccent(activeDept), display: "flex" }}>{node.icon}</span>
                                  <span style={{ fontSize: isMobile ? 9 : 10.5, fontWeight: 800, color: T.black, textAlign: "center", lineHeight: 1.15 }}>{node.name}</span>
                                </div>
                              )}
                              {sel ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 18, height: 18, borderRadius: "50%", background: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <Check size={11} color="#fff" strokeWidth={3} />
                                </div>
                              ) : hasKids ? (
                                <div style={{ position: "absolute", top: 5, right: 5, width: 16, height: 16, borderRadius: "50%", background: "rgba(255,255,255,.9)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <ChevronRight size={10} color={T.red} />
                                </div>
                              ) : null}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };

  /* ═══════════════════════════════════
     BRAND ROW HEADER
  ═══════════════════════════════════ */
  const brandRowHeader = (brandId: string) => {
    const partner = brandMap.get(brandId);
    const shopName = partner?.name || "Grower";
    const shopLogo = partner?.image || appLogo;
    const city = partner?.address?.city;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "26px 0 12px" }}>
        <button onClick={() => goToBrand(brandId)}
          style={{ width: 34, height: 34, borderRadius: "50%", overflow: "hidden", flexShrink: 0, border: `1px solid ${T.border}`, background: "#fff", padding: 0, cursor: "pointer" }}>
          <img src={shopLogo} alt={shopName} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).src = appLogo; }} />
        </button>
        <span onClick={() => goToBrand(brandId)}
          style={{ fontWeight: 900, fontSize: 14, color: T.black, textDecoration: "underline", textDecorationColor: T.red, textDecorationThickness: 2, textUnderlineOffset: 4, cursor: "pointer" }}>
          {shopName}
        </span>
        {city && (
          <span style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, color: T.muted }}>
            <MapPin size={10} /> {city}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <motion.button whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.92 }} onClick={() => goToBrand(brandId)}
          aria-label={`Open ${shopName}`}
          style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: "#fff", boxShadow: "0 3px 10px rgba(27,14,6,.18)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
          <ChevronRight size={16} color={T.red} />
        </motion.button>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCE CARD — StoreProducts' card, with fresh signals
  ═══════════════════════════════════ */
  const dietDots = (item: any) => {
    const dots: React.ReactNode[] = [];
    const dot = (key: string, title: string, bg: string, color: string, node: React.ReactNode) => (
      <span key={key} title={title} style={{ width: 18, height: 18, borderRadius: 6, background: bg, color, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        {node}
      </span>
    );
    if (item.isVegan) dots.push(dot("vg", "Vegan", HEART.greenSoft, HEART.green, <Leaf size={10} />));
    else if (item.isVegetarian) dots.push(dot("v", "Vegetarian", HEART.greenSoft, HEART.green, <Sparkles size={10} />));
    if (item.isGlutenFree) dots.push(dot("gf", "Gluten-free", HEART.goldSoft, HEART.goldDark, <WheatOff size={10} />));
    if ((item.spiceLevel ?? 0) > 0) dots.push(dot("sp", SPICE[item.spiceLevel] || "Spicy", HEART.redSoft, HEART.redDark, <Flame size={10} />));
    return dots;
  };

  const produceCard = (item: any) => {
    const soldOut = isOutOfStock(item);
    const q = soldOut ? 0 : getQty(item.listingId);
    const summary = ratingSummaries[item.listingId];
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;
    const accent = brandAccentColor(brandMap.get(item.brandId));
    const fresh = freshnessChip(item);
    const measure = measureLabel(item);

    return (
      <div key={item.listingId} className="p-card" onClick={() => openDetail(item)}
        style={{
          background: T.cardBg, border: `1.5px solid ${T.border}`, borderRadius: 16, padding: 14,
          cursor: "pointer", position: "relative", display: "flex", flexDirection: "column",
          opacity: soldOut ? 0.55 : 1,
          filter: soldOut ? "grayscale(0.85)" : "none",
          transition: "opacity .2s, filter .2s",
        }}>
        {soldOut ? (
          <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: T.mutedDim, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 6, letterSpacing: ".04em" }}>
            SOLD OUT
          </span>
        ) : discount && (
          <span style={{ position: "absolute", top: 10, left: 10, zIndex: 2, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 6 }}>
            {discount}% OFF
          </span>
        )}
        {item.isOrganic && (
          <span style={{ position: "absolute", top: 10, right: 10, zIndex: 2, display: "flex", alignItems: "center", gap: 4, background: HEART.greenSoft, color: HEART.green, fontSize: 10, fontWeight: 800, padding: "3px 9px", borderRadius: 999 }}>
            <Leaf size={10} /> Organic
          </span>
        )}

        <div style={{ width: "100%", aspectRatio: "1/1", borderRadius: 12, overflow: "hidden", background: HEART.goldSoft, marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {item.photos?.[0]
            ? <img src={item.photos[0]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover", filter: soldOut ? "grayscale(1)" : "none" }}
                onError={(e) => {
                  const el = e.currentTarget as HTMLImageElement;
                  el.src = appLogo; el.style.objectFit = "contain"; el.style.opacity = "0.4";
                }} />
            : <Sprout size={32} color={HEART.green} style={{ opacity: .4 }} />}
        </div>

        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.black, lineHeight: 1.3, marginBottom: 2, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as any, overflow: "hidden", minHeight: 34 }}>
          {item.name}
        </div>

        <div style={{ fontSize: 11.5, color: T.muted, marginBottom: 6, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <span>{measure || catName(item.categoryId) || "item"}</span>
          {summary?.totalReviews ? <span>· {summary.avgRating.toFixed(1)}★ ({summary.totalReviews})</span> : null}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 8, minHeight: 18, flexWrap: "wrap" }}>
          {dietDots(item)}
          {!soldOut && fresh && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9.5, fontWeight: 800, color: fresh.color }}>
              <Clock size={9} /> {fresh.text}
            </span>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "auto" }}>
          <span style={{ fontSize: 15, fontWeight: 800, color: T.black }}>
            {price(item.rate ?? 0)}
          </span>
          {soldOut ? (
            <span style={{ fontSize: 10.5, fontWeight: 800, color: T.mutedDim, letterSpacing: ".04em" }}>
              Unavailable
            </span>
          ) : q > 0 ? (
            <div onClick={(e) => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 6, background: HEART.goldSoft, borderRadius: 999, padding: "3px 6px" }}>
              <button className="fsh-plus" aria-label="Remove one"
                onClick={(e) => { e.stopPropagation(); removeLine(item, null, []); }}
                style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Minus size={12} />
              </button>
              <span style={{ fontSize: 12, fontWeight: 800, color: T.black, minWidth: 12, textAlign: "center" }}>{q}</span>
              <button className="fsh-plus" aria-label="Add one" onClick={(e) => quickAdd(item, e)}
                style={{ width: 22, height: 22, borderRadius: "50%", border: "none", background: accent, color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Plus size={13} />
              </button>
            </div>
          ) : (
           <button className="fsh-plus" aria-label={`Add ${item.name} to basket`}
              onClick={(e) => quickAdd(item, e)}
              style={{ width: 30, height: 30, borderRadius: "50%", border: "none", background: accent, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: `0 3px 10px ${accent}59` }}>
              <Plus size={16} color="#fff" />
            </button>
          )}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════
     PRODUCT ROWS BY GROWER
  ═══════════════════════════════════ */
  const emptyState = (title: string, body: string, cta: string, action: () => void) => (
    <div style={{ textAlign: "center", padding: 56 }}>
      <div style={{ fontSize: 48, marginBottom: 14 }}>🥬</div>
      <h3 style={{ fontWeight: 900, fontSize: 22, color: T.black, marginBottom: 10 }}>{title}</h3>
      <p style={{ color: T.muted, marginBottom: 24, fontSize: 13 }}>{body}</p>
      <button onClick={action}
        style={{ padding: "12px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 800, cursor: "pointer", fontSize: 13 }}>
        {cta}
      </button>
    </div>
  );

  const renderProducts = (isMob: boolean) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 4, height: 22, borderRadius: 2, background: GRAD_H }} />
          <span style={{ fontWeight: 900, fontSize: 15, color: T.black }}>
            {activeFilterName ?? (locationDenied ? "All produce" : showGlobal ? "Global" : userCity ? `Near ${userCity}` : userCountry ?? "All produce")}
          </span>
          <span style={{ fontSize: 12, color: T.muted, background: HEART.goldSoft, padding: "2px 9px", borderRadius: 999 }}>
            {visibleCount} items · {productsByBrand.length} growers
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={activeSort} onChange={(e) => setActiveSort(e.target.value as SortKey)} aria-label="Sort produce"
            style={{ border: T.outline, borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 700, color: T.muted, background: "#fff", cursor: "pointer", outline: "none" }}>
            <option value="default">Featured</option>
            <option value="freshest">Freshest first</option>
            <option value="price_asc">Price: low → high</option>
            <option value="price_desc">Price: high → low</option>
            <option value="name_asc">Name A–Z</option>
          </select>
          {locationActive && (
            <button onClick={handleToggleGlobal}
              style={{ display: "flex", alignItems: "center", gap: 5, background: showGlobal ? HEART.bg : T.redDim, borderRadius: 999, padding: "5px 13px", border: `1.5px solid ${showGlobal ? HEART.border : RGBA_RED(0.3)}`, fontSize: 11, fontWeight: 700, color: showGlobal ? T.mutedDim : T.red, cursor: "pointer" }}>
              {showGlobal ? <MapPin size={11} /> : <Globe size={11} />}{showGlobal ? "Show local" : "Show global"}
            </button>
          )}
        </div>
      </div>

      {productsByBrand.length === 0 ? (
        filteredProducts.length === 0 ? (
          activeFilterName
            ? emptyState(`No ${activeFilterName} in stock`, "Pick another category, or switch department on the bar above.", "Clear filters", resetAllFilters)
            : emptyState(`No ${dept?.name ?? "fresh"} listings yet`, "Nobody is selling in this department right now — try another one on the bar above.", "Clear filters", resetAllFilters)
        ) : locationDenied ? (
          emptyState("Turn on location", "We use it to show growers who can actually deliver to you.", "Enable location", () => setLocationModalOpen(true))
        ) : (
          emptyState(
            showGlobal ? `No global ${dept?.name ?? "produce"} right now` : `No local ${dept?.name ?? "produce"} right now`,
            `Switch to ${showGlobal ? "local" : "global"} above, or clear your filters.`,
            "Clear filters",
            resetAllFilters
          )
        )
      ) : (
        productsByBrand.map((group) => (
          <div key={group.brandId}>
            {brandRowHeader(group.brandId)}
            <div style={isMob ? S.mobileGrid : S.grid}>
              {group.items.map((item: any) => produceCard(item))}
            </div>
          </div>
        ))
      )}
    </div>
  );

  /* ═══════════════════════════════════
     PRODUCE DETAIL — sheet on mobile, modal on desktop.
     Sizes (one) · extras (many) · any unspecified category
     option · qty · handling · Add to basket / Buy now.
  ═══════════════════════════════════ */
  const detailModal = () => {
    if (!detailItem) return null;

    const item = detailItem;
    const photos: string[] = item.photos?.length ? item.photos : [];
    const summary = ratingSummaries[item.listingId];
    const hasRating = summary && summary.totalReviews > 0;

    const sizes = portionsOf(item);
    const extras = extrasOf(item);

    /* only what the grower saved — colour swatches on their own,
       everything else as plain rows */
    const savedAttrs = savedAttrsOf(item);
    const colorAttrs = savedColorsOf(item);
    const detailRows = savedAttrs
      .filter(({ def }) => def.inputType !== "color")
      .map(({ def, raw }) => ({ name: def.name, value: resolveAttrValue(def, raw) }))
      .filter((r) => r.value);

    const stock = item.stock ?? 0;
    const soldOut = isOutOfStock(item);
    const cartId = buildCartId(item, selectedSize, selectedExtras);
    const inCart = cartItems.find((i: any) => i.listingId === cartId)?.qty ?? 0;
    const rt: RateType = (item?.rateType as RateType) || DEFAULT_RATE_TYPE;
    const rawUnit = computeItemPrice(item, selectedSize, selectedExtras);
    const unitPrice = fx(rawUnit);
    const lineTotal = `${cur}${fx(calculateLineTotal(rawUnit, qty, rt)).toFixed(2)}`;

    const fresh = freshnessChip(item);
    const measure = measureLabel(item);
    const storage = STORAGE_META[String(item.storage ?? "").toLowerCase()];
    const StorageIcon = storage?.icon ?? Package;
    const allergens: string[] = Array.isArray(item.allergens) ? item.allergens : [];
    const discount = item.compareAtPrice && item.compareAtPrice > (item.rate ?? 0)
      ? Math.round(((item.compareAtPrice - (item.rate ?? 0)) / item.compareAtPrice) * 100) : null;

    const canAdd = !soldOut;

    const closeBtn = (
      <button onClick={closeDetail} aria-label="Close"
        style={{ background: "rgba(255,255,255,.92)", border: "none", width: 30, height: 30, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", boxShadow: "0 2px 8px rgba(27,14,6,.18)" }}>
        <X size={15} color={T.black} />
      </button>
    );

    const chip = (key: string, bg: string, color: string, icon: React.ReactNode, text: string) => (
      <span key={key} style={{ display: "flex", alignItems: "center", gap: 4, background: bg, color, fontSize: 11, fontWeight: 800, padding: "5px 10px", borderRadius: 999 }}>
        {icon} {text}
      </span>
    );

    const content = (
      <div style={{ background: "#fff", borderRadius: isMobile ? "24px 24px 0 0" : 20, overflow: "hidden", maxWidth: isMobile ? "100%" : 470, width: "100%", maxHeight: isMobile ? "88vh" : "84vh", display: "flex", flexDirection: "column" }}>
        <div style={{ height: 4, background: GRAD_H, backgroundSize: "200% 100%", animation: "gradSpin 4s ease infinite", flexShrink: 0 }} />
        {isMobile && <div style={{ width: 36, height: 4, borderRadius: 2, background: HEART.border, margin: "12px auto 0", flexShrink: 0 }} />}

        <div style={{ overflowY: "auto", padding: "18px 20px 0" }}>
          {/* gallery */}
          {photos.length > 0 ? (
            <div style={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: 14, overflow: "hidden", marginBottom: 16, background: HEART.bg }}>
              <img src={photos[imgIndex]} alt={item.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              {discount && (
                <div style={{ position: "absolute", top: 10, left: 10, background: T.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "3px 10px", borderRadius: 999 }}>{discount}% OFF</div>
              )}
              <div style={{ position: "absolute", top: 10, right: 10 }}>{closeBtn}</div>
              {photos.length > 1 && (
                <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 5 }}>
                  {photos.map((_, i) => (
                    <button key={i} aria-label={`Photo ${i + 1}`} onClick={() => setImgIndex(i)}
                      style={{ width: i === imgIndex ? 16 : 6, height: 6, borderRadius: 3, border: "none", padding: 0, cursor: "pointer", background: i === imgIndex ? T.red : "rgba(255,255,255,.75)", transition: "all .2s" }} />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 10 }}>{closeBtn}</div>
          )}

          {item.categoryId && (
            <p style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", color: T.mutedDim, textTransform: "uppercase", marginBottom: 5, display: "flex", alignItems: "center", gap: 5 }}>
              <Tag size={10} /> {catPathLabel(item.categoryId)}
            </p>
          )}

          <div style={{ fontSize: 16, fontWeight: 800, color: T.black, marginBottom: 8 }}>{item.name}</div>

          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.black }}>{cur}{unitPrice.toFixed(2)}</span>
            {discount && (
              <span style={{ fontSize: 13, color: T.mutedDim, textDecoration: "line-through" }}>{price(item.compareAtPrice ?? 0)}</span>
            )}
            {measure && <span style={{ fontSize: 12, fontWeight: 800, color: T.muted }}>{measure}</span>}
            {hasRating && (
              <span style={{ fontSize: 12, fontWeight: 700, color: T.muted }}>
                {summary.avgRating.toFixed(1)}★ ({summary.totalReviews.toLocaleString()})
              </span>
            )}
          </div>

          {/* handling / diet chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
            {item.isOrganic && chip("org", HEART.greenSoft, HEART.green, <Leaf size={11} />, "Organic")}
            {item.isVegan && chip("vg", HEART.greenSoft, HEART.green, <Leaf size={11} />, "Vegan")}
            {!item.isVegan && item.isVegetarian && chip("v", HEART.greenSoft, HEART.green, <Sparkles size={11} />, "Vegetarian")}
            {item.isGlutenFree && chip("gf", HEART.goldSoft, HEART.goldDark, <WheatOff size={11} />, "Gluten-free")}
            {(item.spiceLevel ?? 0) > 0 && chip("sp", HEART.redSoft, HEART.redDark, <Flame size={11} />, SPICE[item.spiceLevel] || "Spicy")}
            {fresh && chip("fr", `${fresh.color}1A`, fresh.color, <Clock size={11} />, fresh.text)}
            {storage && chip("st", HEART.goldSoft, HEART.goldDark, <StorageIcon size={11} />, storage.label)}
            {item.prepTimeMinutes ? chip("pt", HEART.bg, T.muted, <Clock size={11} />, `${item.prepTimeMinutes} min prep`) : null}
          </div>

          {/* ── SIZES — one choice ── */}
          {sizes.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, textTransform: "uppercase" }}>Size</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.mutedDim }}>Optional — default is {price(item.rate ?? 0)}</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sizes.map((s) => {
                  const on = selectedSize === s.id;
                  return (
                    <button key={s.id} className="fsh-opt" onClick={() => setSelectedSize(on ? null : s.id)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? T.red : T.border}`, background: on ? T.redDim : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? T.red : T.mutedDim}`, background: on ? T.red : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.black }}>
                          {s.name}{s.unit ? ` · ${s.unit}` : ""}
                        </span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? T.red : T.muted }}>
                        {price(s.rate)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── COLOUR — shown only when the grower saved one ── */}
          {colorAttrs.map((c) => (
            <div key={c.id} style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 10, textTransform: "uppercase" }}>
                {c.name}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                {c.swatches.map((s) => (
                  <span key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, border: `1.5px solid ${T.border}`, borderRadius: 999, padding: "6px 13px 6px 7px", background: "#fff" }}>
                    <span style={{ width: 20, height: 20, borderRadius: "50%", background: s.hex || HEART.bg, border: `1px solid ${T.border}`, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: T.black }}>{s.name}</span>
                  </span>
                ))}
              </div>
            </div>
          ))}

          {/* ── EXTRAS — many ── */}
          {extras.length > 0 && (
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.18em", color: T.mutedDim, marginBottom: 10, textTransform: "uppercase" }}>Add extras</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {extras.map((ex) => {
                  const on = selectedExtras.includes(ex.id);
                  return (
                    <button key={ex.id} className="fsh-opt"
                      onClick={() => setSelectedExtras((s) => (on ? s.filter((id) => id !== ex.id) : [...s, ex.id]))}
                      style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "11px 14px", borderRadius: 12, border: `1.5px solid ${on ? T.red : T.border}`, background: on ? T.redDim : "#fff", cursor: "pointer" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ width: 18, height: 18, borderRadius: 6, border: `2px solid ${on ? T.red : T.mutedDim}`, background: on ? T.red : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          {on && <Check size={12} color="#fff" />}
                        </span>
                        <span style={{ fontSize: 13.5, fontWeight: 700, color: T.black }}>{ex.name}</span>
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: on ? T.red : T.muted }}>+{price(ex.rate)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {item.description && (
            <div
              style={{ fontSize: 13, color: T.muted, lineHeight: 1.65, marginBottom: 16 }}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
          )}

          {/* ── what the grower filled in — straight from the category attributes ── */}
          {(() => {
            const rows: [string, string][] = detailRows.map((d) => [d.name, d.value]);
            if (item.calories) rows.push(["Calories", `${item.calories} kcal`]);
            if (item.harvestedOn) rows.push(["Harvested", new Date(item.harvestedOn).toLocaleDateString()]);
            if (item.bestBefore) rows.push(["Best before", new Date(item.bestBefore).toLocaleDateString()]);
            if (item.batchCode) rows.push(["Batch", item.batchCode]);
            if (allergens.length) rows.push(["Allergens", allergens.join(", ")]);
            if (!rows.length) return null;
            return (
              <div style={{ border: `1px solid ${T.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 16 }}>
                {rows.map(([k, v], i) => (
                  <div key={`${k}-${i}`} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "9px 13px", background: i % 2 ? "#fff" : HEART.bg, fontSize: 12 }}>
                    <span style={{ color: T.mutedDim, fontWeight: 700 }}>{k}</span>
                    <span style={{ color: T.black, fontWeight: 700, textAlign: "right" }}>{v}</span>
                  </div>
                ))}
              </div>
            );
          })()}

          {soldOut ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: T.mutedDim, marginBottom: 12 }}>Sold out for now</p>
          ) : stock <= 5 ? (
            <p style={{ fontSize: 11.5, fontWeight: 800, color: T.red, marginBottom: 12 }}>Only {stock} left</p>
          ) : null}

          {!soldOut && (
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.16em", color: T.mutedDim }}>QTY</span>
              <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 12, overflow: "hidden", background: HEART.bg }}>
                <button aria-label="Decrease quantity" onClick={() => setQty((v) => Math.max(1, v - 1))}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: "pointer", color: T.red, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Minus size={14} />
                </button>
                <div style={{ width: 44, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: T.black, borderLeft: `1px solid ${T.border}`, borderRight: `1px solid ${T.border}` }}>{qty}</div>
                <button aria-label="Increase quantity" onClick={() => setQty((v) => Math.min(stock || 99, v + 1))} disabled={qty >= (stock || 99)}
                  style={{ width: 40, height: 40, border: "none", background: "transparent", cursor: qty >= (stock || 99) ? "not-allowed" : "pointer", color: T.red, opacity: qty >= (stock || 99) ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Plus size={14} />
                </button>
              </div>
              {inCart > 0 && (
                <button onClick={() => removeLine(item, selectedSize, selectedExtras)}
                  style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, color: T.red }}>
                  {inCart} in basket — remove one
                </button>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 20px 20px", display: "flex", gap: 10, flexShrink: 0, borderTop: `1px solid ${T.border}` }}>
          <button disabled={!canAdd}
            onClick={() => { addLine(item, selectedSize, selectedExtras, qty); closeDetail(); }}
            style={{ flex: 1, padding: "13px 0", borderRadius: 12, border: `1.5px solid ${T.black}`, background: "transparent", color: T.black, fontWeight: 700, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed", opacity: canAdd ? 1 : 0.45 }}>
            {soldOut ? "Sold out" : "Add to basket"}
          </button>
          <button disabled={!canAdd}
            onClick={() => {
              const line = buildFoodCartLine(item, selectedSize, selectedExtras, qty);
              addLine(item, selectedSize, selectedExtras, qty);
              closeDetail();
              goToCheckout(line);
            }}
            style={{ flex: 1.2, padding: "13px 0", borderRadius: 12, border: "none", background: canAdd ? GRAD_H : "#C8C8C8", color: "#fff", fontWeight: 800, fontSize: 13, cursor: canAdd ? "pointer" : "not-allowed" }}>
            Buy now · {lineTotal}
          </button>
        </div>
      </div>
    );

    return createPortal(
      <AnimatePresence>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeDetail}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.7)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", zIndex: 500 }}>
          {isMobile ? (
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 340, damping: 30 }}
              onClick={(e) => e.stopPropagation()} style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 501 }}>
              {content}
            </motion.div>
          ) : (
            <div style={{ position: "fixed", inset: 0, zIndex: 501, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={(e) => e.stopPropagation()}>
              <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }}
                transition={{ type: "spring", damping: 26, stiffness: 340 }}
                style={{ width: 470, maxWidth: "100%", borderRadius: 20, overflow: "hidden", boxShadow: "0 32px 80px rgba(27,14,6,.35)" }}>
                {content}
              </motion.div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>,
      document.body
    );
  };

  /* ═══════════════════════════════════
     LOCATION MODAL · BOTTOM TABS
  ═══════════════════════════════════ */
  const locationModal = () => (
    <AnimatePresence>
      {locationModalOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLocationModalOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(27,14,6,.8)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <motion.div initial={{ scale: 0.9, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 40 }} onClick={(e) => e.stopPropagation()}
            style={{ width: 320, borderRadius: 20, padding: 24, background: "#fff", boxShadow: "0 30px 80px rgba(27,14,6,.5)", textAlign: "center" }}>
            <div style={{ height: 5, borderRadius: 999, background: GRAD_H, marginBottom: 20 }} />
            <div style={{ fontSize: 32, marginBottom: 10 }}>📍</div>
            <h3 style={{ fontWeight: 900, marginBottom: 8, color: T.black }}>Location is off</h3>
            <p style={{ color: T.muted, margin: "0 0 22px", fontSize: 13, lineHeight: 1.6 }}>
              Allow location in your browser settings to see growers near you.
            </p>
            <button onClick={() => setLocationModalOpen(false)}
              style={{ padding: "11px 28px", borderRadius: 999, border: "none", background: GRAD_H, color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 12 }}>
              Got it
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const bottomTabs = () => (isMobile && userId ? (
    <div style={S.mobileTabs}>
      {[
        { icon: <Home size={22} />, label: "Home", active: true, action: () => navigate(`/FreshStoreHome/${userId}`) },
        { icon: <ShoppingBasket size={22} />, label: "Growers", active: false, action: () => navigate(`/FreshStores/${userId}`) },
        {
          label: "Basket", active: false, icon: (
            <div style={{ position: "relative" }}>
              <ShoppingCart size={22} />
              {cartTotal > 0 && (
                <span style={{ position: "absolute", top: -5, right: -5, background: T.red, color: "#fff", fontSize: 8, fontWeight: 900, width: 16, height: 16, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {cartTotal}
                </span>
              )}
            </div>
          ), action: () => goToCheckout(),
        },
        { icon: <ShoppingBag size={22} />, label: "Orders", active: false, action: () => navigate(`/FreshOrders/${userId}`) },
        { icon: <User size={22} />, label: "Profile", active: false, action: () => navigate(`/profile/${userId}`) },
      ].map((t) => (
        <motion.div key={t.label} whileTap={{ scale: 0.88 } as any} onClick={t.action} className={`tab-item${t.active ? " active" : ""}`}>
          {React.cloneElement(t.icon as any, { color: t.active ? T.red : T.mutedDim })}
          <span>{t.label}</span>
        </motion.div>
      ))}
    </div>
  ) : null);

  /* ═══════════════════════════════════
     RENDER
  ═══════════════════════════════════ */
  return (
    <div style={S.page}>
      <style>{GLOBAL_CSS}</style>
      <PageWatermark />

      <div style={S.content}>
        <header style={S.headerTop}>{headerRow()}</header>

        {mobileCategorySheet()}

        {!isMobile && (
          <div style={{ ...S.desktopGrid, gridTemplateColumns: desktopCatOpen ? "300px 1fr" : "1fr" }}>
            {desktopCatOpen && <aside style={A.sidebar}>{deptTree()}</aside>}
            <main style={{ minWidth: 0 }}>
              {titleBanner()}
              {ovalBar()}
              {categoryIconBar()}
              {freshestBar()}
              {shopByCategory()}
              {renderProducts(false)}
            </main>
          </div>
        )}

        {isMobile && (
          <div style={{ padding: "14px 12px 0" }}>
            {titleBanner()}
            {ovalBar()}
            {categoryIconBar()}
            {freshestBar()}
            <div style={{ marginTop: 16 }}>{shopByCategory()}</div>
            {renderProducts(true)}
          </div>
        )}

        {detailModal()}
        {locationModal()}
        {bottomTabs()}
      </div>
    </div>
  );
};

export default FreshStoreHome;